import { intl } from '@fullstory/intl';
import { FormatNumberOptions } from 'react-intl';

// Figures out the best text to show to a user for a given HTTP response status code and body
export function userErrorMessage(status: number, rsp: any, defaultMsg: string): string {
  // Most/all frontend handlers will return a rsp.Message, but frequently these are just the
  // default HTTP status text, and thus not very useful (nor user-friendly).  Unfortunately we
  // have no reliable way to tell for sure whether the rsp.Message is useful or not, so we use a
  // heuristic; if its is a 500 or 502, we assume the rsp.Message is not helpful, so we
  // use the default; otherwise we hope that a useful rsp.Message was returned so we show that.
  // It is especially important that we show all 402 (Payment Required) errors since these can
  // echo useful errors from Stripe.
  if (status != 500 && status != 502 && rsp && rsp.Message) {
    return rsp.Message;
  }
  return defaultMsg;
}

// Figures out the best text to show to an internal user for a given HTTP
// response status code and body. It is okay to show the details of response
// even when it is a 5xx error.
export function userInternalErrorMessage(status: number, rsp: any): string {
  // If there is a response, then it must be a safe message.
  if (rsp) {
    // Check if it is an error message from the default JSON writer.
    if (rsp.Message) {
      return rsp.Message;
    } else if (typeof rsp === 'string') {
      return rsp;
    }
  }
  return 'Action failed - please try again';
}

// Left-pads a string with some character/string up to minlen.
export function lpad(base: string, minlen: number, padding: string): string {
  let s = base;
  while (s.length < minlen) {
    s = padding + s;
  }
  return s;
}

// Chops the decimal digits of `n`, leaving it with at most `numDigits` digits
// after the decimal point.
export function chopDecimalDigits(n: number, numDigits: number): number {
  if (numDigits == 0) {
    return n | 0; // NB(jaime): Fast integer clamping trick.
  }
  const factor = numDigits * 10;
  const choppedN = Math.floor(n * factor) / factor;
  const asInt = choppedN | 0;
  return choppedN > asInt ? choppedN : asInt;
}

export function getPercentString(numerator: number, denominator: number): string {
  const pct = numerator / denominator;
  let pctString: string;
  if (pct < 0.01 && pct > 0) {
    pctString = intl.formatMessage(
      {
        defaultMessage: '< {percent, number, ::percent}',
        id: '2l55Zg',
        description: "'less than x percent'",
      },
      { percent: 0.01 },
    );
  } else if (pct > 0.99 && pct < 1) {
    pctString = intl.formatMessage(
      {
        defaultMessage: '> {percent, number, ::percent}',
        id: 'pfmq2j',
        description: "'greater than x percent'",
      },
      { percent: 0.99 },
    );
  } else if (Number.isNaN(pct) || !Number.isFinite(pct)) {
    // Dividing by 0 gives us NaN or Infinity
    pctString = intl.formatNumber(0, { style: 'percent' });
  } else {
    pctString = intl.formatNumber(pct, { style: 'percent' });
  }
  return pctString;
}

/**
 * Calculates the percentage change between two values. Percentage change is different,
 * and not to be confused with, percentage difference.
 * @see {@link https://www.mathsisfun.com/data/percentage-difference-vs-error.html}
 * @param value - The current value
 * @param pastValue - The past value with which to compare the current value
 * @param abs - If set to true, returns the absolute value of the percentage change
 * @param alwaysShowSignDisplay - If set to true, the percentage will always show the sign (e.g +10%)
 * @returns a tuple of the [string, number], where the string value is the display string, and the number is the numeric value
 */
export function getPercentageChange(
  value: number,
  pastValue: number,
  abs: boolean = false,
  formatNumberOptions?: Omit<FormatNumberOptions, 'style'>,
): [string, number] {
  const fractionChange = fractionalChange(value, pastValue);
  if (isNaN(fractionChange)) return ['', fractionChange];
  const fraction = abs ? Math.abs(fractionChange) : fractionChange;
  let percentageString: string;

  if (Math.abs(fraction) < 0.01 && value !== pastValue) {
    // The percentage change is not zero but rounds to zero.
    percentageString = intl.formatMessage(
      {
        defaultMessage: '< {percent, number, ::percent}',
        id: '2l55Zg',
        description: "'less than x percent'",
      },
      { percent: 0.01 },
    );
  } else {
    percentageString = intl.formatNumber(fraction, {
      ...formatNumberOptions,
      style: 'percent',
    });
  }
  return [percentageString, Math.round(fraction * 100)];
}

export function fractionalChange(value: number, pastValue: number): number {
  return (value - pastValue) / Math.abs(pastValue);
}

export function numberWithCommas(x: number): string {
  if (typeof x == 'undefined' || x == null) {
    console.warn('Received an undefined or nil value to numberWithCommas.');
    return '0';
  }
  return intl.formatNumber(x);
}

// Produces stringifications for numbers of the form:
// 100, 1.1K, 10.1k, 100.1k, 1.1M, 10.1M, 100.1M, 1.1B
// In other words, for any number over 1000, it will shorten it to at most 1
// decimal place with <maximumFractionDigits> significant figures for numbers < 1
// billion.
export function shortNumber(
  value: number,
  opts?: Pick<FormatNumberOptions, 'minimumFractionDigits' | 'maximumFractionDigits'>,
): string {
  const { minimumFractionDigits, maximumFractionDigits } = opts ?? {};

  if (
    maximumFractionDigits !== undefined &&
    (maximumFractionDigits > 3 || maximumFractionDigits < 0)
  ) {
    // Maybe a better solution than throwing an error?
    throw new Error('Invalid maximumFractionDigits value, it must be in the range of 0 to 3');
  }

  return intl.formatNumber(value, {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits,
    // defaults to 1
    maximumFractionDigits: maximumFractionDigits ?? 1,
  });
}

export function shortOrCommaNumber(
  x: number,
  opts?: { shortenOver?: number; decimalPlaces?: number },
): string {
  const options = { shortenOver: 1000000, decimalPlaces: 1, ...opts };
  if (x >= options.shortenOver) {
    return shortNumber(x, { maximumFractionDigits: options.decimalPlaces });
  } else {
    return numberWithCommas(x);
  }
}

// Return the string form of the number (i.e. "five") for numbers 9 and under.
export function wordForSingleNumbers(x: number): string {
  switch (x) {
    case 0:
      return 'zero';
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    case 9:
      return 'nine';
    default:
      return String(x);
  }
}

export function capitalize(word: string): string {
  if (word.length == 0) {
    return '';
  }
  return word[0].toLocaleUpperCase() + word.substring(1);
}

function isVowel(character: string): boolean {
  return (
    character === 'a' ||
    character === 'e' ||
    character === 'i' ||
    character === 'o' ||
    character === 'u'
  );
}

export function getArticle(noun: string): string {
  return isVowel(noun.charAt(0)) ? 'an' : 'a';
}

// This doesn't work for every verb so should be used with caution
export function constructPresentParticiple(verb: string): string {
  if (!verb.length || verb.length === 1) {
    return '';
  }
  // e.g., "jog" => "jogging"
  if (isVowel(verb.charAt(verb.length - 2))) {
    const last = verb.charAt(verb.length - 1);
    return `${verb}${last}ing`;
  }
  // e.g., "invite" => "inviting"
  if (isVowel(verb.charAt(verb.length - 1))) {
    return `${verb.substring(0, verb.length - 1)}ing`;
  }
  // e.g., "check" => "checking"
  return `${verb}ing`;
}

// This doesn't work for every verb so should be used with caution
export function constructPastParticiple(verb: string): string {
  if (!verb.length || verb.length === 1) {
    return '';
  }
  // e.g., "jog" => "jogged"
  if (isVowel(verb.charAt(verb.length - 2))) {
    const last = verb.charAt(verb.length - 1);
    return `${verb}${last}ed`;
  }
  // e.g., "invite" => "invited"
  if (verb.charAt(verb.length - 1) === 'e') {
    return `${verb}d`;
  }
  // e.g., "check" => "checked"
  return `${verb}ed`;
}

// Returns the ordinal indicator of a number.
export function ordinalIndicator(n: number): string {
  const i = n % 10;
  const j = n % 100;

  if (i == 1 && j != 11) {
    return 'st';
  }
  if (i == 2 && j != 12) {
    return 'nd';
  }
  if (i == 3 && j != 13) {
    return 'rd';
  }
  return 'th';
}

export function ellipsis(str: string, maxChars: number) {
  const tmp = str.substring(0, maxChars);
  return tmp != str ? `${tmp}...` : str;
}

// Class as in CSS
type Class = void | null | string | { [c: string]: boolean };

/**
 * Utility to generate a classname string from a list of conditional classnames
 * Example: classNames("one", {two: false, three: true}) => "one three"
 */
export function classNames(...classes: Class[]): string {
  let result: string[] = [];
  for (const c of classes) {
    if (typeof c === 'string') {
      result.push(c);
    } else if (c) {
      const obj = c;
      result = result.concat(Object.keys(obj).filter(cc => obj[cc]));
    }
  }
  return result.join(' ');
}

// bestUnitFitFromMs takes a value in milliseconds and returns a string that's fit to the best unit. For example, the
// value 892 will return the string '892 ms', and the value 1432 will return the string '1.432 ms', with an option to
// truncate the number of digits after the decimal place to max digits.
export function bestUnitFitFromMs(numMs: number, max = 2): string {
  let bestFitIndex = 0;
  const durations = [
    {
      multiplier: 1,
      unit: 'millisecond',
    },
    {
      multiplier: 1e-3,
      unit: 'second',
    },
    {
      multiplier: 1.67e-5,
      unit: 'minute',
    },
    {
      multiplier: 2.78e-7,
      unit: 'hour',
    },
  ];

  for (let i = 0; i < durations.length; i++) {
    const tmp = numMs * durations[i].multiplier;
    if (tmp < 1000 && tmp >= 0.01) {
      bestFitIndex = i;
      break;
    }
  }

  const { multiplier, unit } = durations[bestFitIndex];
  return intl.formatNumber(multiplier * numMs, {
    unit,
    unitDisplay: 'narrow',
    style: 'unit',
    maximumFractionDigits: max,
  });
}

type PercentageNumberFormatOptions = {
  /**
   * Minimum fraction digits to show.
   * 0 by default, percentages that end in .00 will be truncated to a whole number.
   */
  minimumFractionDigits?: number;
  /**
   * Maximum fraction digits to show.
   * 2 by default.
   */
  maximumFractionDigits?: number;
};

/**
 * Percentage is a helper to convert a number (0-1) into a readable number with a percent sign.
 *
 * @param floatPercentage the current float (0-1) to be converted into a percentage.
 * @param showZeroValue By default, a zero value will show as `--`, set this to true to have it display 0.
 * @param minimumFractionDigits Minimum fraction digits to show. 0 by default, percentages that end in .00 will be truncated to a whole number.
 * @param maximumFractionDigits Maximum fraction digits to show. 2 by default.
 */
export const percentage = (
  floatPercentage: number,
  showZeroValue?: boolean,
  numberFormatOptions: PercentageNumberFormatOptions = {},
) => {
  if (!floatPercentage && !showZeroValue)
    return intl.formatMessage({
      defaultMessage: '--',
      id: 'CP2rdq',
      description: 'This is being used to signify that a numerical value is 0',
    });

  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = numberFormatOptions;

  return intl.formatNumber(floatPercentage, {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

export const shorten = (s: string, length = 80, ellipsis = '...') => {
  if (s.length <= length) {
    return s;
  }
  const max = length - ellipsis.length;
  return s.slice(0, max) + ellipsis;
};
